<template>
  <d-container fluid class="main-content-container px-4">
    <!-- Page Header -->
    <d-row no-gutters class="page-header py-4">
      <d-col col sm="6" class="text-center text-sm-left mb-4 mb-sm-0">
        <span class="text-uppercase page-subtitle">
          {{ $t('product.products') }}
        </span>
        <h3 class="page-title">
          {{ $t('productCategories.addCategory') }}
        </h3>
      </d-col>
      <d-col
        col
        sm="6"
        class="text-right text-sm-right d-flex justify-content-end align-items-end">
        <d-button
          theme="secondary"
          @click.prevent="$router.push('/kategorie-produktow')"
          class="mr-1">
          <i class="material-icons">arrow_back</i>
          {{ $t('ui.backToList') }}
        </d-button>
        <d-button
          theme="success"
          @click.prevent="validateForm">
          <i class="material-icons">edit</i>
          {{ $t('productCategories.editCategory') }}
        </d-button>
      </d-col>
    </d-row>

    <!-- Form -->
    <d-form>
      <validation-observer ref="category-edit-form" tag="form">
        <d-card class="mb-5">
          <d-tabs card v-if="!loadError">
            <!-- Dane ogólne -->
            <d-tab
              :title="$t('ui.generalInfo')"
              active>
              <h4 class="mb-3 pb-3 border-bottom">
                {{ $t('ui.generalInfo') }}
              </h4>

              <div class="form-group">
                <label>
                  {{ $t('productCategories.name') }}:
                </label>

                <validation-provider
                  rules="required"
                  name="name"
                  v-slot="{ errors }">
                  <d-input
                    :state="errors.length ? 'invalid' : null"
                    v-model="name" />

                  <d-form-invalid-feedback>
                    {{ errors[0] }}
                  </d-form-invalid-feedback>
                </validation-provider>
              </div>

              <div class="form-group">
                <label>
                  {{ $t('productCategories.parentCategory') }}:
                </label>

                <div :class="{
                  'form-control': true,
                  'is-select': true,
                }">
                  <v-select
                    :options="flatCategories"
                    v-model="parent_id"
                    :reduce="category => category.id"
                    :placeholder="$t('productCategories.noParentCategory')"
                    label="name"
                    :clearable="true">
                    <template slot="no-options">
                      {{ $t('productCategories.noCategoriesToDisplay') }}&hellip;
                    </template>
                    <template slot="option" slot-scope="option">
                      <div class="d-center">
                        {{ option.name }}
                      </div>
                    </template>
                    <template slot="selected-option" slot-scope="option">
                      <div class="selected d-center">
                        {{ option.name }}
                      </div>
                    </template>
                  </v-select>
                </div>
              </div>
            </d-tab>

            <d-tab :title="$t('productCategories.translations')">
              <h4 class="pb-3 mb-0 border-bottom d-flex">
                {{ $t('productCategories.translations') }}

                <d-button
                  class="ml-auto"
                  theme="success"
                  @click.prevent="addTranslation(id)">
                  <i class="material-icons">add</i>
                  {{ $t('settings.transaltion.addTranslation') }}
                </d-button>
              </h4>

              <table class="table mt-2 mb-0 full-width">
                <thead class="bg-light">
                  <tr>
                    <th scope="col" class="border-0" style="width: 150px;">{{ $t('ui.language') }}</th>
                    <th scope="col" class="border-0" style="width: calc(100% - 350px);">{{ $t('productCategories.name') }}</th>
                    <th scope="col" class="border-0" style="width: 200px;">{{ $t('ui.actions') }}</th>
                  </tr>
                </thead>
                <tbody v-if="category_translations.length">
                  <tr
                    v-for="(translation, index) of category_translations"
                    :key="'category-translation-' + index">
                    <td class="align-middle">
                      {{ getLanguageByID(translation.language_id) }}
                    </td>
                    <td class="align-middle">
                      {{ translation.name }}
                    </td>
                    <td class="align-middle">
                      <d-button
                        theme="primary"
                        @click.prevent="editTranslation(translation)"
                        class="mr-2"
                        :title="$t('ui.edit')">
                        <i class="material-icons">edit</i>
                      </d-button>
                      <d-button
                        theme="danger"
                        :disabled="removeTranslationInProgress"
                        @click.prevent="removeTranslation(translation.id)"
                        :title="$t('ui.delete')">
                        <i class="material-icons">delete</i>
                      </d-button>
                    </td>
                  </tr>
                </tbody>
                <tbody v-else>
                  <tr>
                    <td colspan="3" class="text-center">
                      {{ $t('productCategories.noTranslationsForThisCategory') }}&hellip;
                    </td>
                  </tr>
                </tbody>
              </table>
            </d-tab>
          </d-tabs>

          <div v-if="!allDataLoaded" class="pb-3 pt-3 text-center overlay-loading">
            {{ $t('ui.dataLoading') }}&hellip;
          </div>

          <d-alert
            v-if="loadError"
            show
            theme="warning">
            {{ $t('ui.dataLoadingFailed') }} <a href="javascript:window.location.reload();" class="alert-link">{{ $t('ui.refreshPage') }}</a> {{ $t('ui.toTryAgain') }}
          </d-alert>
        </d-card>
      </validation-observer>
    </d-form>

    <product-category-translation-new-popup
      v-if="showAddTranslationPopup"
      :itemID="selectedItemID"
      :languages="languages" />

    <product-category-translation-edit-popup
      v-if="showEditTranslationPopup"
      :translationData="selectedTranslation"
      :languages="languages" />
  </d-container>
</template>

<script>
import FormUtils from './../../utils/FormUtils.js';
import ListUtils from './../../utils/ListUtils.js';
import ProductCategoryTranslationNewPopup from './ProductCategoryTranslationNewPopup';
import ProductCategoryTranslationEditPopup from './ProductCategoryTranslationEditPopup';

export default {
  name: 'category-edit',
  components: {
    'product-category-translation-new-popup': ProductCategoryTranslationNewPopup,
    'product-category-translation-edit-popup': ProductCategoryTranslationEditPopup
  },
  computed: {
    allDataLoaded () {
      return this.loadedData && this.loadedCategories && this.loadedLanguages;
    },
    flatCategories() {
      let result = [];
      let stack = this.categories.map(category => ({ ...category, prefix: '' }));
      let currentCategoryID = parseInt(this.$route.params.id, 10);

      while (stack.length > 0) {
        let current = stack.shift();

        if (current.id === currentCategoryID) {
          // eslint-disable-next-line
          continue;
        }

        result.push({
          id: current.id,
          name: current.prefix + current.name
        });

        if (current.children && current.children.length > 0) {
          stack = [
            ...current.children.map(child => ({ ...child, prefix: current.prefix + '- ' })),
            ...stack
          ];
        }
      }

      return result;
    }
  },
  data() {
    return {
      name: '',
      parent_id: null,
      category_translations: [],
      categories: [],
      languages: [],
      loadedCategories: false,
      loadedData: false,
      loadedLanguages: false,
      loadError: false,
      selectedItemID: false,
      showAddTranslationPopup: false,
      showEditTranslationPopup: false,
      selectedTranslation: 0,
      removeTranslationInProgress: false
    };
  },
  mounted () {
    this.loadData();
    this.loadLanguages();
    this.loadCategories();
    this.$bus.$on('product-category-translation-new-popup-close', this.closeAddTranslationPopup);
    this.$bus.$on('product-category-translation-edit-popup-close', this.closeEditTranslationPopup);
    this.$bus.$on('product-categories-reload-translations', this.loadTranslations);
  },
  methods: {
    loadData() {
      FormUtils.loadItemData(this, {
        endpointBase: '/categories/item/',
        instanceFields: ['name', 'parent_id', 'category_translations'],
        loadedKey: 'loadedData',
        errorKey: 'loadError',
        successAction () {
          if (this.parent_id === 0) {
            this.parent_id = null;
          }
        }
      });
    },
    validateForm () {
      FormUtils.validate(this.$refs['category-edit-form'], this.submitForm);
    },
    submitForm () {
      let formDataToSend = {
        name: this.name,
        parent_id: this.parent_id === null ? 0 : this.parent_id
      };

      FormUtils.submit({
        $http: this.$http,
        endpoint: '/categories/update/' + this.id,
        formData: formDataToSend,
        successTitle: this.$t('productCategories.editCategorySuccessTitle'),
        successText: this.$t('productCategories.editCategorySuccessText'),
        errorTitle: this.$t('ui.errorOccured'),
        errorText: this.$t('productCategories.editCategoryErrorText'),
        successAction: () => {
          FormUtils.loadItemData(this, {
            endpointBase: '/categories/item/',
            instanceFields: ['name', 'parent_id'],
            loadedKey: 'loadedData',
            errorKey: 'loadError'
          });
        }
      });
    },
    loadCategories () {
      FormUtils.loadAdditionalData(this, {
        method: 'get',
        endpoint: '/categories/items',
        loadedKey: 'loadedCategories',
        outputKey: 'categories',
        errorKey: 'loadError'
      });
    },
    loadLanguages () {
      FormUtils.loadAdditionalData(this, {
        method: 'get',
        endpoint: '/languages/items',
        loadedKey: 'loadedLanguages',
        outputKey: 'languages',
        errorKey: 'loadError'
      });
    },
    getLanguageByID (languageID) {
      let filteredLanguages = this.languages.filter(language => language.id === languageID);

      if (filteredLanguages.length) {
        return filteredLanguages[0].name;
      }

      return '';
    },
    loadTranslations () {
      FormUtils.loadItemData(this, {
        endpointBase: '/categories/item/',
        instanceFields: ['category_translations'],
        loadedKey: null
      });
    },
    addTranslation (itemID) {
      this.selectedItemID = itemID;
      this.showAddTranslationPopup = true;
    },
    editTranslation (translationData) {
      this.selectedTranslation = JSON.parse(JSON.stringify(translationData));
      this.showEditTranslationPopup = true;
    },
    closeAddTranslationPopup () {
      this.showAddTranslationPopup = false;
    },
    closeEditTranslationPopup () {
      this.showEditTranslationPopup = false;
    },
    removeTranslation (translationID) {
      this.removeTranslationInProgress = true;
      let endpointUrl = '/categories/translations/delete/';

      ListUtils.removeItem({
        $http: this.$http,
        endpointBase: endpointUrl,
        id: translationID,
        successTitle: this.$t('settings.transaltion.deleteTranslationSuccessTitle'),
        successText: this.$t('settings.transaltion.deleteTranslationSuccessText'),
        confirmTitle: this.$t('settings.transaltion.deleteTranslationConfirmTitle'),
        confirmText: this.$t('ui.deleteConfirmText'),
        errorTitle: this.$t('ui.errorOccured'),
        errorText: this.$t('settings.transaltion.deleteTranslationErrorText'),
        successAction: () => {
          this.removeTranslationInProgress = false;
          this.loadTranslations();
        },
        cancelAction: () => {
          this.removeTranslationInProgress = false;
        }
      });
    }
  },
  beforeDestroy () {
    this.$bus.$off('product-category-translation-new-popup-close', this.closeAddTranslationPopup);
    this.$bus.$off('product-category-translation-edit-popup-close', this.closeEditTranslationPopup);
    this.$bus.$off('product-categories-reload-translations', this.loadTranslations);
  }
};
</script>

<style lang="scss" scoped>
.new-form-input {
  width: 300px;
}
</style>
